/**
 * This file contains main window commands for issues.
 *
 * To add another command, define a new subclass and append the
 * class to the default export at the bottom of the file.
 */
import {MainWindowCommand} from '../main-window-command'
import type {ModalDialogElement} from '@primer/view-components/app/components/primer/alpha/modal_dialog'
import {isFeatureEnabled} from '@github-ui/feature-flags'

function moveCursorToEnd(input: HTMLTextAreaElement | HTMLInputElement) {
  input.focus()
  input.selectionStart = input.selectionEnd = input.value.length
}

export class EditIssueBody extends MainWindowCommand {
  override title = 'Edit issue body'
  override icon = 'pencil-color-fg-muted'

  issueBody() {
    return document.querySelector('.js-command-palette-issue-body')
  }

  isIssue() {
    return !!this.issueBody()
  }

  override isApplicable() {
    return this.isIssue()
  }

  override run() {
    // Build an edit button (the normal one isn't always loaded)
    const editButton = document.createElement('button')
    editButton.hidden = true
    editButton.classList.add('js-comment-edit-button')

    const issueBody = document.querySelector('div.js-comment')
    issueBody?.appendChild(editButton)

    editButton.click()
    editButton.remove()

    setTimeout(() => {
      const textArea = issueBody?.parentElement?.querySelector<HTMLTextAreaElement>('textarea.js-comment-field')

      if (textArea) {
        moveCursorToEnd(textArea)
      }
    }, 0)
  }
}

export class EditIssueTitle extends MainWindowCommand {
  override title = 'Edit issue title'
  override icon = 'pencil-color-fg-muted'

  issueBody() {
    return document.querySelector('.js-command-palette-issue-body')
  }

  isIssue() {
    return !!this.issueBody()
  }

  override isApplicable() {
    return this.fetchButton() instanceof HTMLButtonElement && this.isIssue()
  }

  fetchButton() {
    return document.querySelector<HTMLButtonElement>('.js-title-edit-button')
  }

  override run() {
    this.fetchButton()?.click()

    // Even though the input has an autofocus, it isn't focused when it is unhidden.
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>('input#issue_title[autofocus]')

      if (input) {
        moveCursorToEnd(input)
      }
    }, 0)
  }
}

export class AddTasklist extends MainWindowCommand {
  override title = 'Add tasklist to issue'
  override icon = 'plus-circle-color-fg-muted'

  issueBody() {
    return document.querySelector('.js-command-palette-issue-body')
  }

  isIssue() {
    return !!this.issueBody()
  }

  override isApplicable() {
    return isFeatureEnabled('tasklist_block') && this.isIssue()
  }

  override run() {
    // Build an edit button (the normal one isn't always loaded)
    const editButton = document.createElement('button')
    editButton.hidden = true
    editButton.classList.add('js-comment-edit-button')

    const issueBody = document.querySelector('div.js-comment')
    issueBody?.appendChild(editButton)

    editButton.click()
    editButton.remove()

    setTimeout(() => {
      const textArea = issueBody?.parentElement?.querySelector<HTMLTextAreaElement>('textarea.js-comment-field')

      if (textArea) {
        let tasklistMarkup = document.createTextNode(
          '```[tasklist]\n- [ ] Create a draft issue or type # to select an issue\n```',
        )
        if (textArea.value !== '') {
          tasklistMarkup = document.createTextNode(
            '\n\n```[tasklist]\n- [ ] Create a draft issue or type # to select an issue\n```',
          )
        }
        moveCursorToEnd(textArea)
        textArea?.appendChild(tasklistMarkup)
      }
    }, 0)
  }
}

class TransferIssue extends MainWindowCommand {
  override title = 'Transfer issue…'
  override icon = 'arrow-right-color-fg-muted'

  override isApplicable() {
    return this.fetchDetails() instanceof HTMLDetailsElement
  }

  fetchDetails() {
    return document.querySelector<HTMLDetailsElement>('details.js-transfer-issue')
  }

  override run() {
    const transferIssueDetails = this.fetchDetails()

    if (transferIssueDetails) {
      const focusMenu = () => {
        setTimeout(() => {
          transferIssueDetails.querySelector<HTMLElement>('[data-menu-button]')?.focus()
        }, 0)
      }

      transferIssueDetails.querySelector<HTMLElement>('include-fragment')?.addEventListener('load', focusMenu)

      transferIssueDetails.open = true
      focusMenu()
    }
  }
}

class LockIssue extends MainWindowCommand {
  constructor() {
    super()
    const isLockCommand = this.isLock()
    this.title = `${isLockCommand ? 'Lock' : 'Unlock'} conversation`
    this.icon = `${isLockCommand ? 'lock' : 'key'}-color-fg-muted`
  }

  override isApplicable() {
    return this.fetchDetails() instanceof HTMLDetailsElement
  }

  isLock() {
    return document.querySelector('summary.lock-toggle-link')?.textContent?.trim() === 'Lock conversation'
  }

  fetchDetails() {
    return document.querySelector<HTMLDetailsElement>('details.js-lock-issue')
  }

  override run() {
    const lockIssueDetails = this.fetchDetails()
    if (lockIssueDetails) {
      lockIssueDetails.open = true

      setTimeout(() => {
        document.querySelector<HTMLSelectElement>('#unlock-reason')?.focus()
      }, 0)
    }
  }
}

class DeleteIssue extends MainWindowCommand {
  override title = 'Delete issue…'
  override icon = 'trash-color-fg-muted'

  override isApplicable() {
    return this.fetchDetails() instanceof HTMLDetailsElement
  }

  fetchDetails() {
    return document.querySelector<HTMLDetailsElement>('details.js-delete-issue')
  }

  override run() {
    const deleteIssueDetails = this.fetchDetails()
    if (deleteIssueDetails) {
      deleteIssueDetails.open = true

      setTimeout(() => {
        deleteIssueDetails.querySelector<HTMLButtonElement>('button[type="submit"]')?.focus()
      }, 0)
    }
  }
}

export class ConvertToDiscussion extends MainWindowCommand {
  override title = 'Convert issue to discussion…'
  override icon = 'comment-discussion-color-fg-muted'

  override isApplicable() {
    return this.fetchConvertButton() instanceof HTMLButtonElement
  }

  fetchConvertButton() {
    return document.querySelector<HTMLButtonElement>('button#dialog-show-convert-to-discussion')
  }

  fetchDialog() {
    return document.querySelector<ModalDialogElement | HTMLDialogElement>('#convert-to-discussion')
  }

  override run() {
    const convertToDiscussionButton = this.fetchConvertButton()
    const convertToDiscussionDialog = this.fetchDialog()
    if (convertToDiscussionButton && convertToDiscussionDialog) {
      const focusMenu = () => {
        setTimeout(() => {
          convertToDiscussionDialog?.querySelector<HTMLElement>('#convert-to-discussion-category')?.focus()
        }, 0)
      }

      convertToDiscussionDialog.querySelector<HTMLElement>('include-fragment')?.addEventListener('load', focusMenu)
      if (convertToDiscussionDialog instanceof HTMLDialogElement) {
        convertToDiscussionDialog.showModal()
      } else {
        convertToDiscussionDialog.open = true
      }
      focusMenu()
    }
  }
}

export default [EditIssueTitle, EditIssueBody, AddTasklist, LockIssue, TransferIssue, DeleteIssue, ConvertToDiscussion]
